import React from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from '@weezevent/nacre';
import { UploadDropZone } from '../../nacre/uploadButton';
import './components.gallery.scss';
import ModalEditImage from '../../components/upload/components/modalEditImage';
import UploadRequest from './request';
import { fetcher } from '@weezevent/weezjs-core';

const textInfoDropzone = function(label, sublabel) {
    return (
        <>
            <div>{label}</div>
            {sublabel && <div>{sublabel}</div>}
        </>
    );
};

export default function UploadImage({
    organization,
    noClick = false,
    onDrop = () => {},
    disabled = false,
    onUpload = () => {},
    fileTypes = UploadImage.fileTypes,
    sublabelDropzone = null,
    icon = 'upload',
    label = null,
    title = null,
    maxSize = 5242880,
    fileType = 'image',
    onClose = () => {},
    onError = () => {},
    cropModalProps = {
        ratio: null,
        forceRatio: false,
        minWidth: 180,
        maxWidth: Infinity,
        minHeight: 180,
        maxHeight: Infinity
    },
    errors = null
}) {
    const { t } = useTranslation();
    const [fileErrors, setFileErrors] = React.useState([]);
    const [imgToUpload, setImgToUpload] = React.useState(null);
    const [isUploading, setIsUploading] = React.useState(false);

    const _onDrop = React.useCallback(
        (files, rejected) => {
            if (rejected.length) {
                setFileErrors(rejected[0]['errors'].map(({ code }) => code));
            } else {
                setFileErrors([]);
            }
            const [image] = files;

            if (!image) {
                return;
            }
            if (image) {
                onDrop();
                setImgToUpload(image);
            }
        },
        [setFileErrors, onDrop]
    );

    const _onUpload = React.useCallback(
        (cropImage = null) => {
            const image = cropImage || imgToUpload;
            setIsUploading(true);
            const authorizationURL = `${window.weezConfig.api.apiUrl}/sites/organizations/${organization.id}/${fileType === 'file' ? 'files/' : ''}gallery`;
            const uploadRequest = new UploadRequest(authorizationURL, { fetch: fetcher });

            return uploadRequest
                .uploadFile(image)
                .then(({ key, href }) => {
                    function getMeta(url, callback) {
                        let img = new Image();
                        img.src = url;
                        img.onload = function() {
                            callback({ width: `${this.width}`, height: `${this.height}` });
                        };
                    }
                    getMeta(href, data => {
                        onUpload(href, data);
                        setIsUploading(false);
                    });
                })
                .catch(e => onError(e))
                .finally(() => {
                    setImgToUpload(null);
                    onClose();
                });
        },
        [onUpload, imgToUpload, organization, onClose]
    );

    return (
        <>
            <ModalEditImage
                isOpen={Boolean(imgToUpload)}
                imageFile={imgToUpload}
                isSubmitting={isUploading}
                onClose={() => {
                    setImgToUpload(null);
                    onClose();
                }}
                onSubmit={_onUpload}
                onSelectFile={_onDrop}
                fileTypes={fileTypes}
                maxSize={maxSize}
                fileErrors={fileErrors}
                {...cropModalProps}
            />
            <div className="wz-upload-zone-file-container">
                {title && <Form.Label label={title} optionalText={t('ticket.common.labelOptional')} />}
                <UploadDropZone
                    noClick={noClick}
                    onFileSelected={_onDrop}
                    disabled={disabled}
                    acceptedFileExtensions={fileType === 'file' ? [] : fileTypes}
                    icon={icon}
                    maxSize={maxSize}
                    label={label || ''}
                    isUploading={isUploading}
                    textInfo={textInfoDropzone(t('ticket.event.minisite.colors_options.dropzone_files'), sublabelDropzone)}
                />

                {fileErrors.map(errorCode => {
                    return (
                        <div className={'dropzone-error-over-limit'} key={errorCode}>
                            {errors?.[errorCode]}
                        </div>
                    );
                })}
            </div>
        </>
    );
}

UploadImage.fileTypes = ['.jpg', '.jpeg', '.png', '.gif'];
